import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import endpoints from '@app/src/api/endpoints'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import {
  findAssessmentForRow,
  getAssessmentRows,
  getEffectiveAssessmentRow,
} from '@app/src/pages/TopicalRisk/Drawer/assessmentResultLogic'
import { getActivatedAssessmentTemplateIds } from '@app/src/pages/TopicalRisk/Drawer/topicalRiskDrawerUtils'
import type Assessment from '@app/src/types/assessment'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import { useMemo } from 'react'

/**
 * The candidate assessment rows for a topic risk and the provider's assessment behind the one that
 * fed the calculation.
 */
export function useEffectiveAssessment(sustainabilityTopicRisk: SustainabilityTopicRisk) {
  const { assessmentSettings } = useFetchAssessmentSettings()

  const hasCandidateAssessment = Boolean(
    sustainabilityTopicRisk.assessmentTemplateId || sustainabilityTopicRisk.assessmentTemplateSectionId,
  )

  const { items: assessments = [], isLoading: isLoadingAssessments } = useFetchCollectionWithPost<Assessment>({
    key: FetchKey.AssessmentCollection,
    endpoint: endpoints.assessmentCollection,
    payload: {
      filter: [
        {
          name: 'providerId',
          filters: [{ operator: Operators.EqualTo, value: sustainabilityTopicRisk.providerId }],
        },
      ],
      include: [],
    },
    options: { enabled: hasCandidateAssessment },
  })

  const activatedAssessmentTemplateIds = useMemo(
    () => getActivatedAssessmentTemplateIds(assessmentSettings),
    [assessmentSettings],
  )

  const rows = useMemo(
    () => getAssessmentRows(sustainabilityTopicRisk, activatedAssessmentTemplateIds),
    [sustainabilityTopicRisk, activatedAssessmentTemplateIds],
  )

  const effectiveRow = getEffectiveAssessmentRow(rows)
  const effectiveAssessment = effectiveRow ? findAssessmentForRow(effectiveRow, assessments) : undefined

  return { rows, effectiveRow, effectiveAssessment, assessments, isLoadingAssessments }
}
